import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import "./Supplier.css";
import axiosInstance from "../../../../Config/axiosInstance";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import AddSupplier from "./CreateSupplier";
import EditSupplier from "./EditSupplier";
import DetailsSupplier from "./DetailsSupplier";
import NotAuthorized from "../../../../Components/ProtectedRoute/NotAuthorized";

const getPathFromUrl = (url) => {
  try {
    const { pathname } = new URL(url);
    return pathname;
  } catch (e) {
    console.error("Eroare la extragerea URL-ului:", e);
    return url;
  }
};

const SupplierList = ({ canCreate, canEdit, canView }) => {
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allowedDepartments, setAllowedDepartments] = useState([]);
  const [supplierStatuses, setSupplierStatuses] = useState([]);
  const navigate = useNavigate();
  const [pmDeptId, setPmDeptId] = useState(null);
  const [procurementDeptId, setProcurementDeptId] = useState(null);

  useEffect(() => {
    axiosInstance.get("/department/user/")
      .then(response => setAllowedDepartments(response.data.map(d => d.value)))
      .catch(error => console.error("Eroare la obtinerea departamentelor permise: ", error));
  }, []);

  useEffect(() => {
    axiosInstance.get("/departments/")
      .then(response => {
        setDepartments(response.data);
        // Găsim ID-urile pentru departamentele de interes
        const pmDept = response.data.find(d => d.name === "PM");
        const procurementDept = response.data.find(d => d.name === "Procurement");
        if (pmDept) setPmDeptId(pmDept.id);
        if (procurementDept) setProcurementDeptId(procurementDept.id);
      })
      .catch(error => console.error("Eroare la încărcarea departamentelor:", error));
  }, []);

  useEffect(() => {
    axiosInstance.get("/status-supplier-department/")
      .then(response => setSupplierStatuses(response.data))
      .catch(error => console.error("Eroare la încărcarea statusurilor:", error));
  }, []);

  useEffect(() => {
    axiosInstance.get("/suppliers/")
      .then(response => {
        let dataWithId = response.data.map((item, index) => ({
          ...item,
          id: item.id || index + 1
        }));

        // Filtrare pe baza departamentelor permise
        if (allowedDepartments.length > 0) {
          dataWithId = dataWithId.filter(supplier =>
            supplier.departments?.some(deptId => allowedDepartments.includes(deptId))
          );
        }

        // Sortează descrescător după ID
        dataWithId = dataWithId.sort((a, b) => b.id - a.id);

        // Generează numerotare vizuală descrescătoare
        const total = dataWithId.length;
        dataWithId = dataWithId.map((item, index) => ({
          ...item,
          nr_crt: total - index,
        }));

        setRows(dataWithId);
      })
      .catch(error => console.error("Eroare la încărcarea furnizorilor:", error));
  }, [allowedDepartments]);


  const baseColumns = [
    { field: "nr_crt", headerName: "No.", flex: 0.5, minWidth: 60, headerClassName: "bold-header" },
    { field: "name", headerName: "Name", flex: 1.2, minWidth: 210, headerClassName: "bold-header" },
    { field: "fiscal_code", headerName: "CUI", flex: 1, minWidth: 180, headerClassName: "bold-header" },
    { field: "phone", headerName: "Phone", flex: 1, minWidth: 150, headerClassName: "bold-header" },
    { field: "email", headerName: "Email", flex: 1.2, minWidth: 170, headerClassName: "bold-header" },
  ];

  let conditionalColumns = [];
  if (pmDeptId && allowedDepartments.includes(pmDeptId)) {
    conditionalColumns.push({
      field: "departments",
      headerName: "Supplier Department",
      flex: 1.2,
      minWidth: 210,
      headerClassName: "bold-header",
      renderCell: (params) => {
        if (departments.length === 0) return "Loading...";
        return params.row.departments
          .map(deptId => departments.find(d => d.id === deptId)?.name || "")
          .join(", ");
      }
    });

  }
  if (procurementDeptId && allowedDepartments.includes(procurementDeptId)) {
    conditionalColumns.push({
      field: "order_count",
      headerName: "Orders",
      flex: 1,
      minWidth: 100,
      headerClassName: "bold-header",
    });
  }

  const finalColumns = [
    ...baseColumns,
    ...conditionalColumns,
    {
      field: "status",
      headerName: "Status",
      flex: 2,
      minWidth: 120,
      headerClassName: "bold-header",
      renderCell: (params) => {
        const supplierId = params.row.id;
        const relevantStatus = supplierStatuses.find(
          status => status.supplier === supplierId &&
            allowedDepartments.includes(status.department)
        );

        const value = relevantStatus?.status || "N/A";
        const isActive = value === "Active";

        return (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "4px 12px",
            borderRadius: "10px",
            backgroundColor: isActive ? "#2e7d32" : "#a6a6a6",
            color: isActive ? "#ffffff" : "#fff",
            width: "60px",
            maxWidth: "150px",
            fontSize: "0.9rem",
            fontWeight: 500,
            height: "18px",
          }}>
            {value}
          </div>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      headerClassName: "bold-header",
      headerAlign: "center",
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            width: "100%",
          }}
        >
          {canEdit && (
            <Button
              variant="text"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/supplier/edit/${params.row.id}`)}
            />
          )}
          {canView && (
            <Button
              variant="text"
              startIcon={<VisibilityIcon />}
              onClick={() => navigate(`/supplier/details/${params.row.id}`)}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="supplier">
        <div className="header-container">
          <h2>SUPPLIERS</h2>
          <div className="supplier-actions">
            {canCreate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/supplier/create")}
                sx={{ borderRadius: "10px" }}
              >
                Add Supplier
              </Button>
            )}
          </div>
        </div>
        <Box className="supplier-box-subcontainer supplier-page supplier-datagrid">
          <div className="supplier-data-grid-container full-width-height custom-supplier-grid">
            <DataGrid
              rows={rows}
              columns={finalColumns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "3px",
                  paddingBottom: "3px",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>
        </Box>
      </div>
    </>
  );
};

const Supplier = () => {
  const [permissions] = useState(() => {
    const saved = localStorage.getItem("permissions");
    return saved ? JSON.parse(saved).pages : [];
  });

  const hasPermission = (path, checkPermission) => {
    return permissions.some((page) => getPathFromUrl(page.url) === path && checkPermission(page));
  };

  const canCreate = hasPermission('/supplier', (page) => page.can_create);
  const canEdit = hasPermission('/supplier', (page) => page.can_edit);
  const canView = hasPermission('/supplier', (page) => page.can_view);

  return (
    <Routes>
      <Route path="/" element={<SupplierList canCreate={canCreate} canEdit={canEdit} canView={canView} />} />
      <Route path="create" element={canCreate ? <AddSupplier /> : <NotAuthorized />} />
      <Route path="edit/:id" element={canEdit ? <EditSupplier /> : <NotAuthorized />} />
      <Route path="details/:id" element={canView ? <DetailsSupplier /> : <NotAuthorized />} />
    </Routes>
  );
};

export default Supplier;